// Usage tracking (plan Phase 7a, 3 Nov 2026): records each page the signed-in user opens and sends them in batches
// to POST /api/usage/events. The server reduces each path to its pattern and takes the user and company from the
// login token. Renders nothing; any failure is ignored so tracking can never break or slow a page.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const FLUSH_MS = 30000;
const MAX_QUEUE = 50;
let queue = [];
let queueToken = null;
let seq = 0;             // per page load; with the time it keeps two views in the same millisecond apart   // the login that queued the events: a sign-out/sign-in or impersonation switch drops them

export function enqueue(location, now = Date.now()) {
  const token = localStorage.getItem('token');
  if (!token) return;                                     // signed-out pages (login, invite) are not tracked
  if (token !== queueToken) { queue = []; queueToken = token; }
  const tab = new URLSearchParams(location.search || '').get('tab') || '';
  const brand = parseInt(localStorage.getItem('selectedBrandId') || '', 10);
  queue.push({ path: location.pathname, tab, brand_id: Number.isInteger(brand) ? brand : 0, t: now, seq: seq++ });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
}

export function flush(onUnload = false, now = Date.now()) {
  if (!queue.length) return;
  const token = localStorage.getItem('token');
  const events = queue;
  queue = [];
  if (!token || token !== queueToken) return;            // never send one login's page views with another's token
  try {
    if (onUnload && typeof fetch === 'function') {
      // keepalive lets the request finish while the tab closes; axios can't do that
      fetch(`${axios.defaults.baseURL || ''}/api/usage/events`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-auth-token': token },
        body: JSON.stringify({ events, sent_at: now }),
      }).catch(() => {});
    } else {
      axios.post('/api/usage/events', { events, sent_at: now }).catch(() => {});
    }
  } catch (_) { /* never break the page */ }
}

export function _resetForTests() { queue = []; queueToken = null; seq = 0; }
export function _queueForTests() { return queue; }

export default function UsageTracker() {
  const location = useLocation();

  useEffect(() => {
    try { enqueue(location); } catch (_) { /* never break the page */ }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setInterval(() => flush(false), FLUSH_MS);
    const onHide = () => { if (document.visibilityState === 'hidden') flush(true); };
    const onPageHide = () => flush(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  return null;
}
